import type { Install, Rollout } from './types';

type Translate = (key: string, options?: any) => string;

const t = (translate: Translate, key: string, options?: any) => translate(`resources.kiosk-releases.${key}`, options);

export function rolloutPauseLine(rollout: Rollout, translate: Translate): string | null {
  if (rollout.status !== 'paused' || rollout.pauseReason === null) return null;
  if (rollout.pauseReason === 'manual') return t(translate, 'pauseReasons.manual', { name: rollout.pausedByEmail === null ? '-' : rollout.pausedByEmail });
  return t(translate, `pauseReasons.${rollout.pauseReason}`);
}

export function rolloutTargetLabel(rollout: Rollout, translate: Translate): string {
  if (rollout.targetType === 'all') return t(translate, 'rollout.targets.all');
  if (rollout.targetType === 'group') return t(translate, 'rollout.targetGroup', { name: rollout.targetName === null ? String(rollout.targetId) : rollout.targetName });
  return rollout.targetName === null ? String(rollout.targetId) : rollout.targetName;
}

export function waitingReasonLabel(reason: string, notBefore: string | null, translate: Translate): string {
  return t(translate, `waitingReasons.${reason}`, reason === 'not_before' ? { time: notBefore === null ? '-' : new Date(notBefore).toLocaleString() } : undefined);
}

export function installStateLabel(install: Pick<Install, 'state' | 'waitingReason' | 'blockedReason'>, notBefore: string | null, translate: Translate): string {
  if (install.blockedReason !== null) return t(translate, `blocked.${install.blockedReason}`);
  const state = t(translate, `installStates.${install.state}`);
  if (install.state === 'waiting_for_idle' && install.waitingReason !== null) return `${state}: ${waitingReasonLabel(install.waitingReason, notBefore, translate)}`;
  return state;
}

export function updateErrorLabel(code: string, translate: Translate): string {
  return `${t(translate, `updateErrors.${code}`)} (${code})`;
}

type ErrorShape = { errorCode?: string; details?: Record<string, unknown>; message?: string; body?: unknown };

function errorSource(error: ErrorShape | undefined): ErrorShape | undefined {
  if (error !== undefined && error !== null && typeof error.body === 'object' && error.body !== null) return error.body as ErrorShape;
  return error;
}

export function errorLine(error: ErrorShape | undefined, translate: Translate): string {
  const source = errorSource(error);
  const code = source?.errorCode;
  if (code === undefined) return t(translate, 'errors.generic');
  const reason = source?.details?.reason;
  const byReason = typeof reason === 'string' ? translate(`resources.kiosk-releases.errors.byReason.${code}.${reason}`, { _: '' }) : '';
  if (byReason !== '') return byReason;
  const known = translate(`resources.kiosk-releases.errors.${code}`, { _: '' });
  return known === '' ? t(translate, 'errors.generic') : known;
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
